import React, { useState, useEffect } from 'react';
import { FaTimesCircle, FaCheckCircle, FaClock, FaEye } from 'react-icons/fa';
import { Transaction } from '../../types';
import { formatCurrency } from '../../../../../utils/functions';
import TableDateFormatter from '../../../../../components/common/Table/TableDateFormatter';
import { getPaymentReceiptByTransactionId } from '../../../../../crud/payments_api/payment_receipts';
import { toast } from 'react-toastify';
import ReceiptDetails from '../../Receipts/components/Table/components/ReceiptDetails/ReceiptDetails';

interface TableProps {
  data: Transaction[];
}

const TransactionsTable: React.FC<TableProps> = ({ data }) => {
  const [showSelection, setShowSelection] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<Transaction[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [showReceiptDetails, setShowReceiptDetails] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<any>(null);
  const [loadingReceiptDetails, setLoadingReceiptDetails] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSelectTransaction = (transaction: Transaction) => {
    setSelectedTransactions(prev => 
      prev.some(t => t.transaction_id === transaction.transaction_id)
        ? prev.filter(t => t.transaction_id !== transaction.transaction_id)
        : [...prev, transaction]
    );
  };

  const paymentTypeTranslations: { [key: string]: string } = {
    'tarjeta crédito': 'Crédito',
    'tarjeta débito': 'Débito',
  };

  const getPaymentTypeName = (transaction: Transaction) => {
    if (Number(transaction.payment_type_id) === 22 && (transaction as any).custom_payment_name) {
      return (transaction as any).custom_payment_name;
    }
    const paymentTypeName = transaction.payment_type_name;
    const normalizedKey = paymentTypeName?.toLowerCase();
    return paymentTypeTranslations[normalizedKey] || paymentTypeName;
  };

  const getStatusInfo = (transaction: Transaction) => {
    if (transaction.canceled) {
      return { status: 'cancelled', label: 'Cancelado', icon: FaTimesCircle, badge: 'mp-badge mp-badge-danger', accent: 'mp-mobile-card-accent-danger' };
    } else if (transaction.approved) {
      return { status: 'success', label: 'Pagado', icon: FaCheckCircle, badge: 'mp-badge mp-badge-success', accent: 'mp-mobile-card-accent-success' };
    }
    return { status: 'warning', label: 'Pendiente', icon: FaClock, badge: 'mp-badge mp-badge-warning', accent: 'mp-mobile-card-accent-warning' };
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('es-CL', { 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', '');
  };

  const handleOpenReceiptDetails = async (transactionId: string) => {
    const parsedTransactionId = Number(transactionId);

    if (!Number.isFinite(parsedTransactionId)) {
      toast.error('No se pudo identificar el recibo asociado a esta transacción');
      return;
    }

    setLoadingReceiptDetails(true);
    try {
      const response = await getPaymentReceiptByTransactionId(parsedTransactionId);
      setSelectedReceipt(response.data);
      setShowReceiptDetails(true);
    } catch (error) {
      console.error('Error loading receipt details:', error);
      toast.error('No se pudo cargar el detalle del recibo');
    } finally {
      setLoadingReceiptDetails(false);
    }
  };

  const handleCloseReceiptDetails = () => {
    setShowReceiptDetails(false);
    setSelectedReceipt(null);
  };

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm opacity-40" style={{ color: 'var(--text-color)' }}>No hay transacciones aún</p>
      </div>
    );
  }

  const renderDesktopView = () => (
    <div className="mp-table-card">
      <div className="overflow-x-auto">
        <table className="mp-table">
          <thead className="mp-table-head">
            <tr>
              {showSelection && <th className="w-12"></th>}
              <th>Usuario</th>
              <th>Neto</th>
              <th>Propina</th>
              <th>Monto</th>
              <th>Tipo</th>
              <th>Fecha</th>
              <th>Recibo</th>
              <th style={{ textAlign: 'right' }}>Estado</th>
            </tr>
          </thead>
          <tbody className="mp-table-body">
            {data.map(transaction => {
              const statusInfo = getStatusInfo(transaction);
              const StatusIcon = statusInfo.icon;
              return (
                <tr key={transaction.transaction_id} className="cursor-default">
                  {showSelection && (
                    <td className="w-12" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 accent-orange-500"
                        checked={selectedTransactions.some(t => t.transaction_id === transaction.transaction_id)}
                        onChange={() => handleSelectTransaction(transaction)}
                      />
                    </td>
                  )}
                  <td className="font-medium">
                    {transaction.user_name || <span className="opacity-25 italic">—</span>}
                  </td>
                  <td className="tabular-nums">
                    {formatCurrency((transaction as any).net_amount ?? transaction.transaction_amount)}
                  </td>
                  <td className="tabular-nums opacity-60">
                    {formatCurrency((transaction as any).tip_amount ?? 0)}
                  </td>
                  <td>
                    <span className="font-bold tabular-nums" style={{ color: '#FC7556' }}>
                      {formatCurrency(transaction.transaction_amount)}
                    </span>
                  </td>
                  <td>
                    <span className="mp-badge mp-badge-info">{getPaymentTypeName(transaction)}</span>
                  </td>
                  <td className="opacity-50">
                    <TableDateFormatter
                      data={transaction.local_timestamp || transaction.timestamp}
                      isRealTime={false}
                      stopCounting
                      isTimestamp
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--mediopunto-orange)',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleOpenReceiptDetails(transaction.transaction_id)}
                      disabled={loadingReceiptDetails}
                      aria-label="Ver recibo"
                      title="Ver recibo"
                    >
                      <FaEye />
                    </button>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className={statusInfo.badge}>
                      <StatusIcon />
                      {statusInfo.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mp-table-footer">
        <span className="text-xs opacity-35">
          Mostrando <strong className="font-semibold">{data.length}</strong> transacciones
        </span>
      </div>
    </div>
  );

  const renderMobileView = () => (
    <div className="flex flex-col gap-2.5 px-0">
      {data.map(transaction => {
        const statusInfo = getStatusInfo(transaction);
        return (
          <div key={transaction.transaction_id} className={`mp-mobile-card ${statusInfo.accent}`} style={{ color: 'var(--text-color)' }}>
            {/* Row 1: User + Status */}
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-bold truncate">
                {transaction.user_name || '—'}
              </span>
              <span className={statusInfo.badge}>{statusInfo.label}</span>
            </div>
            {/* Row 2: Amounts + Type + Date */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs opacity-45">
                  {formatDate(transaction.local_timestamp || transaction.timestamp)}
                </span>
                <span className="mp-badge mp-badge-info">{getPaymentTypeName(transaction)}</span>
                <span className="text-xs opacity-45 tabular-nums">
                  Neto {formatCurrency((transaction as any).net_amount ?? transaction.transaction_amount)}
                </span>
              </div>
              <span className="text-base font-extrabold tabular-nums" style={{ color: '#FC7556' }}>
                {formatCurrency(transaction.transaction_amount)}
              </span>
            </div>

            <div className="mt-2 flex justify-end">
              <button
                type="button"
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--mediopunto-orange)',
                  cursor: 'pointer'
                }}
                onClick={() => handleOpenReceiptDetails(transaction.transaction_id)}
                disabled={loadingReceiptDetails}
                aria-label="Ver recibo"
                title="Ver recibo"
              >
                <FaEye />
              </button>
            </div>
          </div>
        );
      })}
      <div className="text-center py-3">
        <span className="text-xs opacity-40" style={{ color: 'var(--text-color)' }}>
          Mostrando <strong className="font-semibold">{data.length}</strong> transacciones
        </span>
      </div>
    </div>
  );

  return (
    <>
      {isMobile ? renderMobileView() : renderDesktopView()}

      {selectedReceipt && (
        <ReceiptDetails
          setReceipts={() => { }}
          currentReceipt={selectedReceipt}
          show={showReceiptDetails}
          onClose={handleCloseReceiptDetails}
        />
      )}
    </>
  );
};

export default TransactionsTable;
